import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DashboardApiService } from '../db-api.service';
import {
  CreateMigrationPayload,
  MigrationApiResponse,
  MigrationConfirmation,
  MigrationGroup,
  MigrationGroupsParams,
  MigrationGroupsResponse,
  UpdateMigrationGroupPayload,
} from './skill-frameworks-migration.models';

export * from './skill-frameworks-migration.models';

const MIGRATIONS_ENDPOINT = '/v1/issuer/migrations';

@Injectable({
  providedIn: 'root',
})
export class SkillFrameworksMigrationApiService extends DashboardApiService {
  protected override _getHeaders(method: string, endpoint: string): { [header: string]: string } {
    const headers = super._getHeaders(method, endpoint);
    return {
      ...headers,
      'accredible-department-id': this.embedding.departmentId.toString(),
    };
  }

  createMigration(payload: CreateMigrationPayload): Observable<MigrationApiResponse> {
    return this._post(MIGRATIONS_ENDPOINT, payload).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getMigration(id: string): Observable<MigrationApiResponse> {
    return this._get(`${MIGRATIONS_ENDPOINT}/${id}`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  listMigrationGroups(
    migrationId: string,
    params: MigrationGroupsParams = {},
  ): Observable<MigrationGroupsResponse> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null) {
        httpParams = httpParams.set(key, String(value));
      }
    }

    const base = `${MIGRATIONS_ENDPOINT}/${migrationId}/groups`;
    const queryString = httpParams.toString();
    const url = queryString ? `${base}?${queryString}` : base;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  updateMigrationGroup(
    migrationId: string,
    groupId: number,
    payload: UpdateMigrationGroupPayload,
  ): Observable<MigrationGroup> {
    return this._patch(`${MIGRATIONS_ENDPOINT}/${migrationId}/groups/${groupId}`, payload).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  approveAll(migrationId: string): Observable<{ approved_count: number }> {
    return this._post(`${MIGRATIONS_ENDPOINT}/${migrationId}/groups/approve_all`, {}).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  cancelMigration(migrationId: string): Observable<{ id: string; status: string }> {
    return this._post(`${MIGRATIONS_ENDPOINT}/${migrationId}/cancel`, {}).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getConfirmation(migrationId: string): Observable<MigrationConfirmation> {
    return this._get(`${MIGRATIONS_ENDPOINT}/${migrationId}/confirmation`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  startMigration(migrationId: string): Observable<MigrationApiResponse> {
    return this._post(`${MIGRATIONS_ENDPOINT}/${migrationId}/start`, {}).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }
}
